import { z } from "zod";
import { pageQuerySchema } from "./pagination";

/**
 * Notifications for Eduleo's team (the bell in the console, and email). Events about a school
 * reach everyone who may see that kind of record there (Cerbos decides, with their area);
 * personal ones (your sign-in security, your access) reach only you. Each person chooses, per
 * kind, whether it shows in the console and whether it is emailed to their work email.
 */

export const NOTIFICATION_KINDS = [
  "contract_signature",
  "school_live",
  "school_status",
  "invoice_paid",
  "access_changed",
  "security",
] as const;
export type NotificationKind = (typeof NOTIFICATION_KINDS)[number];

/**
 * Which records a kind is about, which decides who may see it: the same people who may see a
 * school, a contract or an invoice of that school; "personal" is for one person only.
 */
export const NOTIFICATION_TOPICS = ["school", "contract", "invoice", "personal"] as const;
export type NotificationTopic = (typeof NOTIFICATION_TOPICS)[number];

export const NOTIFICATION_TOPIC_OF: Record<NotificationKind, NotificationTopic> = {
  contract_signature: "contract",
  school_live: "school",
  school_status: "school",
  invoice_paid: "invoice",
  access_changed: "personal",
  security: "personal",
};

export const NOTIFICATION_KIND_LABELS: Record<NotificationKind, string> = {
  contract_signature: "A school signs or declines its agreement",
  school_live: "A school goes live",
  school_status: "A school is suspended or reactivated",
  invoice_paid: "An invoice is paid in full",
  access_changed: "Your role or area changes",
  security: "Sign-in security alerts",
};

export const NOTIFICATION_KIND_DESCRIPTIONS: Record<NotificationKind, string> = {
  contract_signature:
    "When a school's signatory signs the subscription agreement Eduleo sent, or declines to.",
  school_live: "When a school finishes onboarding and its people can sign in.",
  school_status: "When a school's access is cut off, or given back.",
  invoice_paid: "When the last payment of an invoice is recorded.",
  access_changed:
    "When a super admin changes your role, permissions or the schools you look after.",
  security:
    "When your password or two-step verification changes. Always on, so you hear about changes you didn't make.",
};

/** Kinds nobody can turn off: you must hear about changes to your own sign-in. */
export const LOCKED_NOTIFICATION_KINDS: readonly NotificationKind[] = ["security"];

/** Emailed unless the person turns it off; the rest are only emailed if they turn it on. */
export const EMAILED_BY_DEFAULT: readonly NotificationKind[] = [
  "contract_signature",
  "access_changed",
  "security",
];

export const notificationKindSchema = z.enum(NOTIFICATION_KINDS, {
  error: "Unknown kind of notification",
});

/** One kind's choices, saved as soon as a switch is flipped. */
export const notificationPreferenceSchema = z.object({
  inApp: z.boolean({ error: "Choose whether to show it in the console" }),
  email: z.boolean({ error: "Choose whether to email it" }),
});

export type NotificationPreferenceUpdate = z.output<typeof notificationPreferenceSchema>;

export const notificationListQuerySchema = pageQuerySchema.extend({
  limit: z.coerce.number().int().min(1).max(50).default(20),
});

export interface NotificationDto {
  id: string;
  kind: NotificationKind;
  /** "Delhi Public School signed contract EDU-C-0001" */
  title: string;
  body: string;
  /** Where to look in the console, e.g. "/platform/schools/DPS-RKP/contracts"; null if nowhere. */
  link: string | null;
  createdAt: string;
  /** Newer than when the person last opened their notifications. */
  unread: boolean;
}

export interface NotificationPageDto {
  items: NotificationDto[];
  nextCursor: string | null;
  unreadCount: number;
}

export interface UnreadCountDto {
  /** Capped at 100: the bell shows "99+" beyond that. */
  unreadCount: number;
}

export interface NotificationPreferenceDto {
  kind: NotificationKind;
  inApp: boolean;
  email: boolean;
  /** Can't be turned off (sign-in security). */
  locked: boolean;
}

export interface NotificationSettingsDto {
  preferences: NotificationPreferenceDto[];
  /** Where emails go: the work email on the Profile page. Null: none yet, so nothing is emailed. */
  email: string | null;
  /** False when this Eduleo OS has no email set up: nothing is emailed to anyone. */
  emailAvailable: boolean;
}

/** The choices that apply when someone hasn't changed a kind. */
export function defaultPreference(kind: NotificationKind): Omit<NotificationPreferenceDto, "kind"> {
  return {
    inApp: true,
    email: EMAILED_BY_DEFAULT.includes(kind),
    locked: LOCKED_NOTIFICATION_KINDS.includes(kind),
  };
}
